/**
 * useProviderViewGuard - Provider view & simulation mode check
 * Mirrors legacy app ProviderViewGuard
 *
 * Checks:
 * - URL exceptions (PreCheckMyScript, AlternativeMedicationsandSwitches)
 * - Simulation mode in sessionStorage
 * - tinLevelExternalChange environment flag
 */

import { GuardResult } from '../types';

// URL paths that bypass provider view check
const PROVIDER_VIEW_EXCEPTIONS = [
  '/PointofCareAssist/PreCheckMyScript',
  '/PointofCareAssist/AlternativeMedicationsandSwitches',
];

// Redirect targets for a missing provider context must never themselves require
// provider context, otherwise the guard redirects into an infinite loop.
const MISSING_CONTEXT_SAFE_PATHS = ['/myhome', '/myinsights', '/pype-home'];

const hasStoredValue = (key: string): boolean => {
  const value = sessionStorage.getItem(key);
  return !!value && value !== 'null' && value !== '[]' && value !== '{}';
};

export function checkProviderViewGuard(
  pathname: string,
  tinLevelExternalChange: boolean,
  isInternal: boolean = true
): GuardResult {
  const currentURL = pathname;

  // Check if current URL is in exception list
  const isException = PROVIDER_VIEW_EXCEPTIONS.some((exc) => currentURL.includes(exc));

  if (isException) {
    return { allowed: true };
  }

  // tinLevelExternalChange: if true, skip only the simulation-mode block below (production
  // setting) — the provider-context check further down must still run for every environment.
  const skipSimulationCheck = tinLevelExternalChange;

  // Check simulation mode in sessionStorage
  const simulationMode = sessionStorage.getItem('simulationMode');
  const isSimulating = simulationMode && simulationMode !== 'false';

  // If user is in simulation mode, they can't access certain routes
  if (!skipSimulationCheck && isSimulating) {
    console.log('X ProviderViewGuard - User in simulation mode, blocking access');
    return { allowed: false, redirect: '/myinsights' };
  }

  // sessionStorage is per-origin, so a host/environment switch (or a cleared session)
  // leaves it empty even though the auth token is still valid. Internal users record
  // their choice in 'providerInfo' (ChangeProviderComponentConnector); external users
  // get 'topFilterTin' from the OHID login flow (authService's hydrateExternalSessionState).
  // Without this check, a direct link into a provider-view route renders with default
  // "All" filters instead of sending the user back to pick a provider/TIN.
  const isMissingContextSafePath = MISSING_CONTEXT_SAFE_PATHS.some((safe) =>
    currentURL.startsWith(safe)
  );

  if (!isMissingContextSafePath) {
    const hasProviderContext = isInternal
      ? hasStoredValue('providerInfo') ||
        hasStoredValue('multiSelectedTins') ||
        (sessionStorage.getItem('contractIdNavigation') === 'true' &&
          hasStoredValue('selectedContractId'))
      : !!sessionStorage.getItem('topFilterTin');

    if (!hasProviderContext) {
      console.log('X ProviderViewGuard - Missing provider context, redirecting');
      return { allowed: false, redirect: isInternal ? '/myhome' : '/myinsights' };
    }
  }

  // Not simulating or tinLevelExternalChange is true -> allow
  return { allowed: true };
}
